import type { TeaserImportance } from '@UI/aside/asideTeaserImportance/AsideTeaserImportance';
import type { ContentHref } from '@generalTypes/apiTypes';
import type { PrivateState } from '@generalTypes/personApiTypes';
import type { ZillGoal, ZillGoalSubDetail } from '@newStore/zill/zillTypes';
import type {
  DropBuildingBlock,
  DropExistingNode,
  DropFileBlock,
  HoverPositionEnum,
  InlineField,
  NodeType,
} from '@nodeTypeConfig/configTypes';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { BACK_TO_DOCUMENTS_LIST, SAVE_DOCUMENT } from '@store/constants/actionTypes';
import { settings } from '../../config/settings';

export type Mode = 'EDIT' | 'SUGGEST' | 'REVIEW';

export type NewNodePlacementData<T extends NodeType> = {
  parentHref: ContentHref;
  positionIndex: number;
  nodeType: T;
};

export type AsideTab = 'metadata' | 'audiences' | 'web';

export type AttachmentGroupModalDataData = NewNodePlacementData<
  NodeType.ATTACHMENTS_GROUP | NodeType.DOWNLOAD
>;

export type GlobalDocumentModalDataData = NewNodePlacementData<NodeType.GLOBAL_DOCUMENT>;

export type ItemReferenceModalDataData = NewNodePlacementData<
  NodeType.PERSON_REFERENCE | NodeType.PRO_NEWSLETTER_EVENT
>;

export type PendingZillIllustrations = {
  goal: ZillGoal;
  subDetail: ZillGoalSubDetail[];
}[];

export type Teaser = {
  $$expanded: {
    $$meta: {
      permalink: ContentHref;
      type: 'CONTENT';
    };
    key: string;
    title: string;
    importance: TeaserImportance;
    type: 'TEASER';
  };
};

export type TeaserModal = {
  open: boolean;
  data: Teaser | null;
  parentHref: ContentHref | null;
};

export type GoalSetItemModalData = {
  open: boolean;
  parentHref: ContentHref | null;
  positionIndex: number | null;
};

export type DocumentUIState = {
  currentDocument: ContentHref | null;
  currentEditingNode: ContentHref | null;
  mode: Mode | null;
  privateState: PrivateState | null;
  collapsedNodes: Record<string, boolean>;
  dropBelowItem: { href: ContentHref; parentHref: ContentHref | undefined } | null;
  nodeThatShouldBeFocused: { field: InlineField; href: ContentHref } | null;
  draggingNodes: DropExistingNode['items'];
  attachmentGroupModalData: {
    open: boolean;
    data: AttachmentGroupModalDataData | null;
  };
  addGlobalDocumentModalData: {
    open: boolean;
    data: GlobalDocumentModalDataData | null;
  };
  addItemReferenceModalData: {
    open: boolean;
    data: ItemReferenceModalDataData | null;
  };
  asideTab: AsideTab;
  extraFieldsModalOpen: boolean;
  newPendingZillIllustrations: PendingZillIllustrations;
  teaserModal: TeaserModal;
  goalSetItemModal: GoalSetItemModalData;
};

export type CollapseAction = PayloadAction<{ href: string; collapsedByDefault: boolean }>;
export type UpdateLastReadAction = PayloadAction<{ doNotShowLastReadUpdatedNotification: boolean }>;
export type DropItemAction = PayloadAction<{
  droppedItems: DropExistingNode['items'] | DropBuildingBlock['items'] | DropFileBlock['items'];
  parentHref: ContentHref;
  siblingHref: ContentHref | null;
  position: HoverPositionEnum;
}>;
export type EditNodeAction = PayloadAction<{
  href: ContentHref;
  config?: { property: string | undefined; component: string | undefined };
}>;

export type AddIsIncludedInDocumentAction = PayloadAction<{
  parentHref: ContentHref;
  includedInDocument: ContentHref;
  position: number;
}>;

const initialState = (): DocumentUIState => ({
  currentDocument: null,
  mode: null,
  currentEditingNode: null,
  privateState: null,
  collapsedNodes: {},
  dropBelowItem: null,
  nodeThatShouldBeFocused: null,
  draggingNodes: [],
  attachmentGroupModalData: {
    open: false,
    data: null,
  },
  addGlobalDocumentModalData: {
    open: false,
    data: null,
  },
  addItemReferenceModalData: {
    open: false,
    data: null,
  },
  asideTab: 'metadata',
  extraFieldsModalOpen: false,
  newPendingZillIllustrations: [],
  teaserModal: {
    open: false,
    data: null,
    parentHref: null,
  },
  goalSetItemModal: {
    open: false,
    parentHref: null,
    positionIndex: null,
  },
});

const documentUISlice = createSlice({
  name: 'documentUI',
  initialState: initialState(),
  reducers: {
    setSelectedDocument(state, action: PayloadAction<{ documentKey: string | undefined }>) {
      const { documentKey } = action.payload;
      // state = initialState();
      state.currentDocument = `/content/${documentKey}` as ContentHref;
      state.mode = null;
    },
    setMode(state, action: PayloadAction<Mode | 'SUGGESTING' | 'REVIEWING'>) {
      if (action.payload === 'SUGGESTING') {
        state.mode = 'SUGGEST';
      } else if (action.payload === 'REVIEWING') {
        state.mode = 'REVIEW';
      } else {
        state.mode = action.payload;
      }
    },
    openPreview: () => {},
    setPrivateState(state, action: PayloadAction<{ privateState: PrivateState }>) {
      state.privateState = action.payload.privateState;
    },
    toggleCollapseNode(state, action: PayloadAction<{ nodeHref: string }>) {
      const { nodeHref } = action.payload;
      state.collapsedNodes[nodeHref] = !state.collapsedNodes[nodeHref];
    },
    setCollapsedNodes(state, action: PayloadAction<{ collapsedNodes: Record<string, boolean> }>) {
      const { collapsedNodes } = action.payload;
      state.collapsedNodes = { ...state.collapsedNodes, ...collapsedNodes };
    },
    toggleAllCollapsed() {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    unCollapseNode(state, action: PayloadAction<{ nodeHref: ContentHref }>) {},
    updateLastRead(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: UpdateLastReadAction,
    ) {
      if (state.privateState) {
        state.privateState.state.lastRead = new Date(
          new Date().getTime() + settings.CLOCK_OUT_OF_SYNC_SAFETY_DELAY,
        ).toISOString();
      }
    },
    setDropBelowItem: (
      state,
      action: PayloadAction<{ href: ContentHref; parentHref: ContentHref | undefined } | null>,
    ) => {
      if (action.payload === null && state.dropBelowItem === null) {
        return;
      }
      if (action.payload === null) {
        state.dropBelowItem = null;
        return;
      }
      state.dropBelowItem = action.payload;
    },
    dropItem: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: DropItemAction,
    ) => {
      state.dropBelowItem = null;
      state.draggingNodes = [];
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    newNodeDropped: (state, action: PayloadAction<{ newHref: ContentHref }>) => {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    autoFillIGoalIdentifier: (state, action: PayloadAction<{ newHref: ContentHref }>) => {},
    setNodeThatShouldBeFocused: (
      state,
      action: PayloadAction<{ field: InlineField; href: ContentHref } | null>,
    ) => {
      state.nodeThatShouldBeFocused = action.payload;
    },
    setDraggingNodes: (state, action: PayloadAction<{ items: DropExistingNode['items'] }>) => {
      state.draggingNodes = action.payload.items;
    },
    editNode: (_state, _action: EditNodeAction) => {
      // work is done in the saga.
    },
    addIsIncludedInDocument(_state, _action: AddIsIncludedInDocumentAction) {},
    openAside: (state, action: PayloadAction<{ href: ContentHref; asideTab?: AsideTab }>) => {
      state.currentEditingNode = action.payload.href;
      state.asideTab = action.payload.asideTab || 'metadata';
    },
    closeAside: (state) => {
      state.asideTab = 'metadata';
      state.currentEditingNode = null;
    },
    openAttachmentGroupModal: (state, action: PayloadAction<AttachmentGroupModalDataData>) => {
      state.attachmentGroupModalData.data = action.payload;
      state.attachmentGroupModalData.open = true;
    },
    closeAttachmentGroupModal: (state) => {
      state.attachmentGroupModalData.open = false;
      state.attachmentGroupModalData.data = null;
    },
    openAddGlobalDocumentModal: (state, action: PayloadAction<GlobalDocumentModalDataData>) => {
      state.addGlobalDocumentModalData.data = action.payload;
      state.addGlobalDocumentModalData.open = true;
    },
    closeAddGlobalDocumentModal: (state) => {
      state.addGlobalDocumentModalData.open = false;
      state.addGlobalDocumentModalData.data = null;
    },
    openAddItemReferenceModal: (state, action: PayloadAction<ItemReferenceModalDataData>) => {
      state.addItemReferenceModalData.data = action.payload;
      state.addItemReferenceModalData.open = true;
    },
    closeAddItemReferenceModal: (state) => {
      state.addItemReferenceModalData.open = false;
      state.addItemReferenceModalData.data = null;
    },
    setAsideTab(state, action: PayloadAction<AsideTab>) {
      state.asideTab = action.payload;
    },
    openExtraFieldsModal: (state) => {
      state.extraFieldsModalOpen = true;
    },
    closeExtraFieldsModal: (state) => {
      state.extraFieldsModalOpen = false;
    },
    setNewPendingZillIllustrations: (state, action: PayloadAction<PendingZillIllustrations>) => {
      state.newPendingZillIllustrations = action.payload;
    },
    openTeaserModal: (state, action: PayloadAction<ContentHref>) => {
      state.teaserModal.open = true;
      state.teaserModal.data = null;
      state.teaserModal.parentHref = action.payload;
    },
    closeTeaserModal: (state, action: PayloadAction<Teaser | null>) => {
      state.teaserModal.open = false;
      state.teaserModal.data = action.payload || null;
    },
    openGoalSetItemModal: (
      state,
      action: PayloadAction<{ parentHref: ContentHref; positionIndex: number }>,
    ) => {
      state.goalSetItemModal.open = true;
      state.goalSetItemModal.parentHref = action.payload.parentHref;
      state.goalSetItemModal.positionIndex = action.payload.positionIndex;
    },
    closeGoalSetItemModal: (state, _action: PayloadAction<ContentHref | null>) => {
      state.goalSetItemModal.open = false;
      state.goalSetItemModal.parentHref = null;
      state.goalSetItemModal.positionIndex = null;
    },
  },
  selectors: {
    selectAreSomeNodesCollapsed: (state) => {
      return Object.values(state.collapsedNodes).some((isCollapsed) => isCollapsed === true);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(SAVE_DOCUMENT, (state) => {
      state.currentEditingNode = null;
    });
    builder.addCase(BACK_TO_DOCUMENTS_LIST, () => {
      return initialState();
    });
  },
});

export const {
  setSelectedDocument,
  setMode,
  openPreview,
  setPrivateState,
  toggleCollapseNode,
  toggleAllCollapsed,
  unCollapseNode,
  setCollapsedNodes,
  updateLastRead,
  setDropBelowItem,
  dropItem,
  newNodeDropped,
  autoFillIGoalIdentifier,
  setNodeThatShouldBeFocused,
  addIsIncludedInDocument,
  setDraggingNodes,
  editNode,
  openAside,
  closeAside,
  openAttachmentGroupModal,
  closeAttachmentGroupModal,
  openAddGlobalDocumentModal,
  closeAddGlobalDocumentModal,
  openAddItemReferenceModal,
  closeAddItemReferenceModal,
  setAsideTab,
  openExtraFieldsModal,
  closeExtraFieldsModal,
  setNewPendingZillIllustrations,
  openTeaserModal,
  closeTeaserModal,
  openGoalSetItemModal,
  closeGoalSetItemModal,
} = documentUISlice.actions;
export default documentUISlice.reducer;

export const { selectAreSomeNodesCollapsed } = documentUISlice.selectors;
